import React, {useEffect, useMemo, useState} from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  TextInput,
  ActivityIndicator
} from 'react-native';
import {LinearGradient} from 'expo-linear-gradient';
import {useDispatch, useSelector} from 'react-redux';
import AsyncStorage from '@react-native-async-storage/async-storage';
import i18n from 'i18n-js';
import {
  getProductionGoal,
  updateProductionGoal
} from '../../../actions/statisticsAction';
import {customColors} from '../../../commons/tests/mocks/mockdata/colors';
import usePolling from './usePolling';

const isGoalAdmin = role => {
  if (!role) return false;
  if (role.make_super_admin) return true;
  return ['Admin', 'Super Admin', 'Super Super Admin'].includes(role.name);
};

// minutes -> "H:MM"
const formatMinutes = totalMinutes => {
  const minutes = Math.max(Number(totalMinutes) || 0, 0);
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours}:${String(mins).padStart(2, '0')}`;
};

const to12Parts = hhmm24 => {
  const [hStr, mStr] = String(hhmm24 || '').split(':');
  let h = parseInt(hStr, 10);
  let m = parseInt(mStr, 10);
  if (Number.isNaN(h)) h = 8;
  if (Number.isNaN(m)) m = 0;
  const period = h >= 12 ? 'PM' : 'AM';
  let h12 = h % 12;
  if (h12 === 0) h12 = 12;
  return {time: `${h12}:${String(m).padStart(2, '0')}`, period};
};

const to24 = (time12, period) => {
  const [hStr, mStr] = String(time12 || '').split(':');
  let h = parseInt(hStr, 10);
  let m = parseInt(mStr, 10);
  if (Number.isNaN(h)) h = 12;
  if (Number.isNaN(m)) m = 0;
  h = Math.min(Math.max(h, 1), 12);
  m = Math.min(Math.max(m, 0), 59);
  let h24 = h % 12;
  if (period === 'PM') h24 += 12;
  return `${String(h24).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

const formatShortClock = hhmm24 => {
  const {time, period} = to12Parts(hhmm24);
  const [hours, minutes] = time.split(':');
  const compactTime = minutes === '00' ? hours : time;
  return `${compactTime} ${period}`;
};

const buildTickValues = () => [11, 9, 7, 6, 4, 2, 0];

const GoalMetric = ({value, label, color, flexValue}) => (
  <View style={[styles.metricCard, {backgroundColor: color, flex: flexValue}]}>
    <Text style={styles.metricValue}>{value}</Text>
    <Text style={styles.metricLabel}>{label}</Text>
  </View>
);

const GoalSideLabel = ({title, value}) => (
  <View style={styles.sideLabel}>
    <Text style={styles.sideLabelTitle}>{title}</Text>
    {value != null ? <Text style={styles.sideLabelValue}>{value}</Text> : null}
  </View>
);

const DailyOrderGoalChart = ({onEditingStateChange}) => {
  const dispatch = useDispatch();
  const response = useSelector(state => state.statistics.productionGoal);
  const data = response?.data || {};

  const [isAdmin, setIsAdmin] = useState(false);
  const [roleLoaded, setRoleLoaded] = useState(false);
  const [formVisible, setFormVisible] = useState(false);
  const [goalInput, setGoalInput] = useState('');
  const [startInput, setStartInput] = useState('8:00');
  const [startPeriod, setStartPeriod] = useState('AM');
  const [endInput, setEndInput] = useState('5:30');
  const [endPeriod, setEndPeriod] = useState('PM');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const isSet = !!data.is_set;
  const hasResponse = response && response.data;

  useEffect(() => {
    (async () => {
      const info = await AsyncStorage.getItem('userInfo');
      const parsed = info ? JSON.parse(info) : null;
      setIsAdmin(isGoalAdmin(parsed && parsed.role));
      setRoleLoaded(true);
    })();
  }, []);

  useEffect(() => {
    dispatch(getProductionGoal());
  }, [dispatch]);

  usePolling(() => {
    dispatch(getProductionGoal());
  }, 5000);

  useEffect(() => {
    if (isSet) {
      setGoalInput(data.daily_goal != null ? String(data.daily_goal) : '');
      const start = to12Parts(data.shift_start || '08:00');
      setStartInput(start.time);
      setStartPeriod(start.period);
      const end = to12Parts(data.shift_end || '17:30');
      setEndInput(end.time);
      setEndPeriod(end.period);
    }
  }, [isSet, data.daily_goal, data.shift_start, data.shift_end]);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    const result = await dispatch(
      updateProductionGoal({
        daily_goal: goalInput,
        shift_start: to24(startInput, startPeriod),
        shift_end: to24(endInput, endPeriod)
      })
    );
    setSaving(false);
    if (result && result.success) {
      setFormVisible(false);
    } else {
      setError((result && result.error) || 'Failed to save goal');
    }
  };

  const goal = Number(data.daily_goal) || 0;
  const completed = Number(data.completed_orders) || 0;
  const remaining = Math.max(goal - completed, 0);
  const elapsed = Number(data.shift_elapsed_minutes) || 0;
  const shiftRemaining = Number(data.shift_remaining_minutes) || 0;
  const shiftTotal = Math.max(elapsed + shiftRemaining, 0);
  const chartTicks = useMemo(() => buildTickValues(), []);
  const goalCompletedFlex = goal > 0 ? Math.max(completed, 0) : 1;
  const goalRemainingFlex = goal > 0 ? Math.max(remaining, 0) : 1;
  const shiftWorkedFlex = shiftTotal > 0 ? Math.max(elapsed, 0) : 1;
  const shiftRemainingFlex = shiftTotal > 0 ? Math.max(shiftRemaining, 0) : 1;
  const goalMet = goal > 0 && remaining <= 0;
  const shiftDone = shiftTotal > 0 && shiftRemaining <= 0;
  const editing = formVisible || (!isSet && isAdmin);

  useEffect(() => {
    if (typeof onEditingStateChange === 'function') {
      onEditingStateChange(editing);
    }
  }, [editing, onEditingStateChange]);

  if (!hasResponse || !roleLoaded) {
    return (
      <View style={styles.loadingWrap}>
        <ActivityIndicator size="large" color={customColors.white} />
      </View>
    );
  }

  if (editing) {
    return (
      <GoalForm
        heading={
          isSet
            ? i18n.t('dashboard.updateProductionGoal')
            : i18n.t('dashboard.setProductionGoal')
        }
        goalInput={goalInput}
        startInput={startInput}
        startPeriod={startPeriod}
        endInput={endInput}
        endPeriod={endPeriod}
        setGoalInput={setGoalInput}
        setStartInput={setStartInput}
        setStartPeriod={setStartPeriod}
        setEndInput={setEndInput}
        setEndPeriod={setEndPeriod}
        onSave={handleSave}
        onCancel={isSet ? () => setFormVisible(false) : null}
        saving={saving}
        error={error}
      />
    );
  }

  if (!isSet) {
    return (
      <View style={styles.loadingWrap}>
        <Text style={styles.noDataText}>{i18n.t('dashboard.goalNoData')}</Text>
      </View>
    );
  }

  return (
    <LinearGradient
      colors={['#171717', '#0f1011', '#161616']}
      style={styles.outerShell}>
      <View style={styles.container}>
        <View style={styles.headerRow}>
          {isAdmin ? (
            <TouchableOpacity
              style={styles.editPill}
              onPress={() => setFormVisible(true)}>
              <Text style={styles.editPillText}>
                {i18n.t('dashboard.updateGoalButton')}
              </Text>
            </TouchableOpacity>
          ) : null}
        </View>

        <View style={styles.chartSurface}>
          <View style={styles.gridOverlay} pointerEvents="none">
            {chartTicks.map((tick, index) => (
              <View key={`tick-${index}`} style={styles.gridRow}>
                <Text style={styles.gridTick}>{tick}</Text>
                <View style={styles.gridLine} />
              </View>
            ))}
          </View>

          <View style={styles.contentArea}>
            <View style={styles.rowBlock}>
              <View style={styles.barRow}>
                <View style={styles.primaryBarTrack}>
                  {goalMet ? (
                    <GoalMetric
                      value={completed}
                      label={i18n.t('dashboard.goalMet')}
                      color="#6eb810"
                      flexValue={1}
                    />
                  ) : (
                    <>
                      {completed > 0 && (
                        <GoalMetric
                          value={completed}
                          label={i18n.t('dashboard.goalCompleted')}
                          color="#6eb810"
                          flexValue={goalCompletedFlex}
                        />
                      )}
                      <GoalMetric
                        value={remaining}
                        label={i18n.t('dashboard.goalRemaining')}
                        color="#4a4a4a"
                        flexValue={goalRemainingFlex}
                      />
                    </>
                  )}
                </View>
                <GoalSideLabel title="GOAL" value={goal} />
              </View>
            </View>

            <View style={styles.rowBlock}>
              <View style={styles.barRow}>
                <View style={styles.primaryBarTrack}>
                  {shiftDone ? (
                    <GoalMetric
                      value={formatMinutes(elapsed)}
                      label={i18n.t('dashboard.shiftDone')}
                      color="#2b34c6"
                      flexValue={1}
                    />
                  ) : (
                    <>
                      {elapsed > 0 && (
                        <GoalMetric
                          value={formatMinutes(elapsed)}
                          label={i18n.t('dashboard.shiftWorked')}
                          color="#2b34c6"
                          flexValue={shiftWorkedFlex}
                        />
                      )}
                      <GoalMetric
                        value={formatMinutes(shiftRemaining)}
                        label={i18n.t('dashboard.goalRemaining')}
                        color="#4a4a4a"
                        flexValue={shiftRemainingFlex}
                      />
                    </>
                  )}
                </View>
                <GoalSideLabel title="FULL" value="SHIFT" />
              </View>
            </View>
          </View>
        </View>

        <View style={styles.footer}>
          <Text style={styles.footerItem}>GOAL:{goal}</Text>
          <Text style={styles.footerItem}>
            START:{formatShortClock(data.shift_start)}
          </Text>
          <Text style={styles.footerItem}>
            END:{formatShortClock(data.shift_end)}
          </Text>
        </View>
      </View>
    </LinearGradient>
  );
};

const TimeField = ({label, time, period, onChangeTime, onTogglePeriod}) => (
  <View style={styles.formRow}>
    <Text style={styles.formLabel}>{label}</Text>
    <TextInput
      style={[styles.formInput, styles.timeInput]}
      value={time}
      onChangeText={onChangeTime}
      placeholder="8:00"
      placeholderTextColor={customColors.MediumGray}
    />
    <TouchableOpacity style={styles.periodToggle} onPress={onTogglePeriod}>
      <Text style={styles.periodText}>{period}</Text>
    </TouchableOpacity>
  </View>
);

const GoalForm = ({
  heading,
  goalInput,
  startInput,
  startPeriod,
  endInput,
  endPeriod,
  setGoalInput,
  setStartInput,
  setStartPeriod,
  setEndInput,
  setEndPeriod,
  onSave,
  onCancel,
  saving,
  error
}) => (
  <View style={styles.formContainer}>
    <Text style={styles.formHeading}>{heading}</Text>

    <View style={styles.formRow}>
      <Text style={styles.formLabel}>{i18n.t('dashboard.goalLabel')}</Text>
      <TextInput
        style={styles.formInput}
        value={goalInput}
        onChangeText={setGoalInput}
        keyboardType="numeric"
        placeholder="150"
        placeholderTextColor={customColors.MediumGray}
      />
    </View>

    <TimeField
      label={i18n.t('dashboard.shiftStart')}
      time={startInput}
      period={startPeriod}
      onChangeTime={setStartInput}
      onTogglePeriod={() => setStartPeriod(startPeriod === 'AM' ? 'PM' : 'AM')}
    />

    <TimeField
      label={i18n.t('dashboard.shiftEnd')}
      time={endInput}
      period={endPeriod}
      onChangeTime={setEndInput}
      onTogglePeriod={() => setEndPeriod(endPeriod === 'AM' ? 'PM' : 'AM')}
    />

    {error ? <Text style={styles.formError}>{error}</Text> : null}

    <View style={styles.formActions}>
      {onCancel && (
        <TouchableOpacity
          style={[styles.formButton, styles.cancelButton]}
          onPress={onCancel}
          disabled={saving}>
          <Text style={styles.formButtonText}>
            {i18n.t('dashboard.goalCancel')}
          </Text>
        </TouchableOpacity>
      )}
      <TouchableOpacity
        style={[styles.formButton, styles.saveButton]}
        onPress={onSave}
        disabled={saving}>
        {saving ? (
          <ActivityIndicator color={customColors.white} />
        ) : (
          <Text style={styles.formButtonText}>
            {i18n.t('dashboard.goalSave')}
          </Text>
        )}
      </TouchableOpacity>
    </View>
  </View>
);

const styles = StyleSheet.create({
  outerShell: {
    flex: 1,
    width: '100%',
    padding: 28
  },
  container: {
    flex: 1,
    backgroundColor: '#111111',
    borderRadius: 2,
    paddingHorizontal: 22,
    paddingTop: 18,
    paddingBottom: 12
  },
  loadingWrap: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center'
  },
  noDataText: {
    color: customColors.white,
    fontSize: 32,
    fontFamily: 'Poppins_600SemiBold'
  },
  headerRow: {
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 42,
    marginBottom: 16
  },
  title: {
    color: customColors.white,
    fontSize: 28,
    fontFamily: 'Poppins_400Regular',
    letterSpacing: 0.4
  },
  editPill: {
    position: 'absolute',
    right: 0,
    top: 0,
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderRadius: 999,
    backgroundColor: '#222222'
  },
  editPillText: {
    color: customColors.white,
    fontSize: 12,
    fontFamily: 'Poppins_600SemiBold'
  },
  chartSurface: {
    flex: 1,
    backgroundColor: '#0a1216',
    borderRadius: 6,
    overflow: 'hidden',
    paddingTop: 22,
    paddingBottom: 20,
    paddingLeft: 0,
    paddingRight: 0
  },
  gridOverlay: {
    ...StyleSheet.absoluteFillObject,
    paddingTop: 10,
    paddingBottom: 18
  },
  gridRow: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center'
  },
  gridTick: {
    width: 44,
    color: customColors.white,
    fontSize: 12,
    fontFamily: 'Poppins_600SemiBold',
    textAlign: 'right',
    paddingRight: 6,
    opacity: 0.95
  },
  gridLine: {
    flex: 1,
    height: 1,
    backgroundColor: 'rgba(255,255,255,0.15)'
  },
  faintLabelWrap: {
    position: 'absolute',
    top: 52,
    left: 290
  },
  faintLabel: {
    color: 'rgba(255,255,255,0.35)',
    fontSize: 11,
    fontFamily: 'Poppins_400Regular'
  },
  contentArea: {
    flex: 1,
    justifyContent: 'center',
    paddingLeft: 56,
    paddingRight: 18
  },
  rowBlock: {
    marginVertical: 14
  },
  barRow: {
    flexDirection: 'row',
    alignItems: 'center'
  },
  primaryBarTrack: {
    flex: 1,
    height: 108,
    flexDirection: 'row',
    borderRadius: 4,
    overflow: 'hidden'
  },
  metricCard: {
    flex: 1,
    minWidth: 120,
    paddingHorizontal: 6,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 4
  },
  metricValue: {
    color: customColors.white,
    fontSize: 44,
    fontFamily: 'Poppins_600SemiBold',
    lineHeight: 48
  },
  metricLabel: {
    color: customColors.white,
    fontSize: 16,
    fontFamily: 'Poppins_500Medium',
    marginTop: 2
  },
  sideLabel: {
    width: 148,
    paddingLeft: 10,
    justifyContent: 'center'
  },
  sideLabelTitle: {
    color: customColors.white,
    fontSize: 30,
    fontFamily: 'Poppins_600SemiBold',
    lineHeight: 29
  },
  sideLabelValue: {
    color: customColors.white,
    fontSize: 30,
    fontFamily: 'Poppins_600SemiBold',
    lineHeight: 29
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    paddingTop: 4,
    paddingBottom: 8,
    flexWrap: 'wrap'
  },
  footerItem: {
    color: customColors.white,
    fontSize: 14,
    fontFamily: 'Poppins_600SemiBold',
    textDecorationLine: 'underline',
    marginLeft: 18
  },
  formContainer: {
    flex: 1,
    width: '100%',
    maxWidth: 640,
    alignSelf: 'center',
    justifyContent: 'center',
    padding: 24
  },
  formHeading: {
    color: customColors.white,
    fontSize: 34,
    fontFamily: 'Poppins_600SemiBold',
    textAlign: 'center',
    marginBottom: 30
  },
  formRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 18
  },
  formLabel: {
    color: customColors.white,
    fontSize: 24,
    fontFamily: 'Poppins_500Medium',
    width: 160
  },
  formInput: {
    flex: 1,
    backgroundColor: '#1A1D20',
    color: customColors.white,
    fontSize: 24,
    fontFamily: 'Poppins_500Medium',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#444444'
  },
  timeInput: {
    flex: 0,
    width: 120,
    textAlign: 'center'
  },
  periodToggle: {
    marginLeft: 12,
    width: 80,
    paddingVertical: 12,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#444444',
    backgroundColor: '#2C3AED',
    alignItems: 'center'
  },
  periodText: {
    color: customColors.white,
    fontSize: 22,
    fontFamily: 'Poppins_600SemiBold'
  },
  formError: {
    color: '#FF6B6B',
    fontSize: 18,
    fontFamily: 'Poppins_500Medium',
    marginTop: 10
  },
  formActions: {
    flexDirection: 'row',
    justifyContent: 'center',
    marginTop: 30
  },
  formButton: {
    minWidth: 140,
    paddingVertical: 14,
    paddingHorizontal: 24,
    borderRadius: 6,
    alignItems: 'center',
    marginHorizontal: 10
  },
  saveButton: {
    backgroundColor: '#2C3AED'
  },
  cancelButton: {
    backgroundColor: '#444444'
  },
  formButtonText: {
    color: customColors.white,
    fontSize: 22,
    fontFamily: 'Poppins_600SemiBold'
  }
});

export default DailyOrderGoalChart;
